import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Target, ChevronDown } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { getSegmentColor } from '../../utils/segmentColors';
import { getTableTypeConfig, TABLE_TYPES, TableType } from '../../utils/tableUtils';
import { CHAT_CONSTANTS } from '../../constants/chat';

interface DatasetPreviewInlineProps {
  initialParams: {
    searchQuery: string;
    tableType: 'companies' | 'people' | 'custom';
    customTypeDescription?: string;
    rowCount?: number;
  };
  onGenerate: (params: {
    searchQuery: string | null;
    criteria: string[] | null;
    tableType: 'companies' | 'people' | 'custom';
    customType: string;
    rowCount: number;
    uploadId: string | null;
    title: string | null;
    previewData?: any;
  }) => void;
}

export const DatasetPreviewInline: React.FC<DatasetPreviewInlineProps> = ({
  initialParams,
  onGenerate
}) => {
  const [params, setParams] = useState(initialParams);
  const [previewData, setPreviewData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced preview updates when parameters change
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Capture current params to avoid stale closure
    const currentParams = params;
    debounceTimerRef.current = setTimeout(() => {
      loadPreview(currentParams);
    }, CHAT_CONSTANTS.DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [params]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const loadPreview = async (previewParams: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const preview = await apiClient.previewDataset(
        previewParams.searchQuery,
        previewParams.tableType,
        previewParams.customTypeDescription
      );
      setPreviewData(preview);
    } catch (err) {
      setError('Failed to load preview');
      console.error('Preview error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = () => {
    if (!previewData) return;

    // Extract criteria from preview data
    const criteria = previewData.search?.criteria?.map((c: any) => c.description) || null;
    
    // Determine row count (use initial params, preview estimate, or default)
    const rowCount = initialParams.rowCount || previewData.estimatedResults || 100;
    
    // Generate title from search query
    const title = `Dataset: ${params.searchQuery}`;

    onGenerate({
      searchQuery: params.searchQuery || null,
      criteria,
      tableType: params.tableType,
      customType: params.customTypeDescription || '',
      rowCount,
      uploadId: null,
      title,
      previewData
    });
  };

  const handleTypeChange = (type: TableType) => {
    setParams(prev => ({ ...prev, tableType: type }));
    setIsDropdownOpen(false);
  };

  const currentTypeConfig = getTableTypeConfig(params.tableType);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-2 max-w-full">
      <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2">
        <Target className="w-4 h-4" />
        Dataset Preview
      </h4>
      
      {/* Editable Parameters */}
      <div className="space-y-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-blue-700 mb-1">Search Query</label>
          <textarea
            value={params.searchQuery}
            onChange={(e) => setParams(prev => ({ ...prev, searchQuery: e.target.value }))}
            className="w-full p-2 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={2}
            placeholder="Describe what you want to find..."
          />
        </div>
        
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-blue-700 mb-1">Type</label>
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-300 rounded text-sm hover:border-blue-400 transition-colors w-full"
              >
                <currentTypeConfig.icon className="w-4 h-4 text-blue-600" />
                <span className="text-blue-700 font-medium flex-1 text-left">{currentTypeConfig.label}</span>
                <ChevronDown className={`w-4 h-4 text-blue-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsDropdownOpen(false)}
                  />
                  <div
                    className="absolute top-full left-0 mt-1 w-full bg-white border border-blue-200 rounded shadow-lg overflow-hidden z-20"
                  >
                    {TABLE_TYPES.map((type) => {
                      const config = getTableTypeConfig(type);
                      const Icon = config.icon;
                      return (
                        <button
                          key={type}
                          onClick={() => handleTypeChange(type)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${
                            params.tableType === type ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="font-medium">{config.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
          
          {params.tableType === 'custom' && (
            <div className="flex-2">
              <label className="block text-xs font-medium text-blue-700 mb-1">Custom Description</label>
              <input
                type="text"
                value={params.customTypeDescription || ''}
                onChange={(e) => setParams(prev => ({ 
                  ...prev, 
                  customTypeDescription: e.target.value 
                }))}
                placeholder="e.g., AI startups, Tech podcasts..."
                className="w-full p-2 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center gap-2 text-blue-600 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading preview...
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="text-red-600 text-sm py-2">{error}</div>
      )}
      
      {/* Preview Results */}
      {previewData && !isLoading && (
        <div className="space-y-4">
          {/* Search Criteria */}
          {previewData.search?.criteria && previewData.search.criteria.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-blue-800 mb-2 flex items-center gap-2">
                <Target className="w-3.5 h-3.5" />
                Search Criteria
              </h5>
              <div className="space-y-2">
                {previewData.search.criteria.map((criterion: any, index: number) => {
                  const segmentColor = getSegmentColor(index);
                  return (
                    <div
                      key={index}
                      className="bg-white rounded-lg px-3 py-2.5 border-l-3 shadow-sm"
                      style={{
                        borderLeftColor: segmentColor,
                        backgroundColor: `${segmentColor}10`
                      }}
                    >
                      <p className="text-sm text-gray-800">{criterion.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Available Enrichments */}
          {previewData.enrichments && previewData.enrichments.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-blue-800 mb-2">
                Available Data Fields ({previewData.enrichments.length})
              </h5>
              <div className={`grid grid-cols-3 gap-2`}>
                {previewData.enrichments.slice(0, CHAT_CONSTANTS.PREVIEW_MAX_ENRICHMENTS).map((enrichment: any, index: number) => (
                  <div
                    key={index}
                    className="bg-white border border-blue-200 rounded px-2 py-1.5"
                  >
                    <p className="text-xs font-medium text-gray-700">{enrichment.description}</p>
                    <p className="text-xs text-gray-400">{enrichment.format}</p>
                  </div>
                ))}
              </div>
              {previewData.enrichments.length > CHAT_CONSTANTS.PREVIEW_MAX_ENRICHMENTS && (
                <p className="text-xs text-blue-600 mt-2">
                  + {previewData.enrichments.length - CHAT_CONSTANTS.PREVIEW_MAX_ENRICHMENTS} more fields
                </p>
              )}
            </div>
          )}
          
          {/* Estimated Results */}
          {previewData.estimatedResults && (
            <div className="bg-white rounded border border-blue-200 px-3 py-2">
              <span className="text-sm text-blue-800">
                <strong>Estimated Results:</strong> {previewData.estimatedResults}
              </span>
            </div>
          )}
          
          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={isLoading || !params.searchQuery.trim() || (params.tableType === 'custom' && !params.customTypeDescription?.trim())}
            className="w-full bg-blue-600 text-white py-2.5 px-4 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            Generate Dataset
          </button>
        </div>
      )}
    </div>
  );
};